import React, { createContext, useContext, useState, useEffect } from 'react';
import { ApiClient } from '../services/api';

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  phone?: string;
  company?: string;
  role?: string;
  status?: string;
  is_superadmin?: boolean;
}

export interface Workspace {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  is_client: boolean | number;
  client_company_name?: string | null;
  timezone: string;
  is_protected?: boolean | number;
  connected_accounts_count?: number;
  posts_count?: number;
  members_count?: number;
  user_role?: string;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  token: string | null;
  isLoading: boolean;
  login: (identifier: string, pass: string, captchaToken?: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    pass: string,
    company: string,
    mobile?: string,
    workspaceName?: string,
    captchaToken?: string,
    extraFields?: {
      country?: string;
      jobTitle?: string;
      website?: string;
      companySize?: string;
      confirmPassword?: string;
      termsAccepted?: boolean;
      plan_id?: string;
    }
  ) => Promise<any>;
  forgotPassword: (identifier: string, captchaToken?: string) => Promise<any>;
  resetPassword: (payload: { token?: string; identifier?: string; otp_code?: string; new_password?: string; password?: string }) => Promise<any>;
  verifyOtp: (payload: { identifier: string; channel: 'EMAIL' | 'SMS'; purpose: string; code: string }) => Promise<any>;
  resendOtp: (payload: { identifier: string; channel: 'EMAIL' | 'SMS'; purpose: string; captchaToken?: string }) => Promise<any>;
  logout: () => void;
  switchWorkspace: (workspaceId: string) => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('scalebulk_logged_out') === 'true';

  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== 'undefined') {
      if (isLoggedOut) return null;
      const saved = localStorage.getItem('scalebulk_user_data');
      if (saved) {
        try { return JSON.parse(saved); } catch {}
      }
    }
    return null;
  });

  const [workspaces, setWorkspaces] = useState<Workspace[]>(() => {
    if (typeof window !== 'undefined') {
      if (isLoggedOut) return [];
      const saved = localStorage.getItem('scalebulk_workspaces_list');
      if (saved) {
        try { return JSON.parse(saved); } catch {}
      }
    }
    return [];
  });

  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(() => {
    if (typeof window !== 'undefined') {
      if (isLoggedOut) return null;
      const saved = localStorage.getItem('scalebulk_workspace_data');
      if (saved) {
        try { return JSON.parse(saved); } catch {}
      }
    }
    return null;
  });

  const [token, setToken] = useState<string | null>(() => {
    if (isLoggedOut) return null;
    return ApiClient.getToken();
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const refreshUserData = async () => {
    try {
      if (!token) {
        setIsLoading(false);
        return;
      }
      const data = await ApiClient.auth.me();
      if (data && data.user) {
        setUser(data.user);
        setWorkspaces(data.workspaces || []);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('scalebulk_user_data', JSON.stringify(data.user));
          localStorage.setItem('scalebulk_workspaces_list', JSON.stringify(data.workspaces || []));
        }

        const savedWsId = ApiClient.getWorkspaceId() || (typeof localStorage !== 'undefined' ? localStorage.getItem('scalebulk_workspace_id') : null);
        const active = data.workspaces?.find((w: Workspace) => w.id === savedWsId) || data.workspaces?.[0] || null;
        setCurrentWorkspace(active);
        if (active) {
          ApiClient.setWorkspaceId(active.id);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('scalebulk_workspace_data', JSON.stringify(active));
            localStorage.setItem('scalebulk_workspace_id', active.id);
          }
        } else {
          ApiClient.setWorkspaceId(null);
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('scalebulk_workspace_data');
            localStorage.removeItem('scalebulk_workspace_id');
          }
        }
      }
    } catch (err: any) {
      console.warn('[AuthContext] Session verification note:', err.message || err);
      // If token expired or user is no longer active, log out
      if (err.status === 401 || err.status === 403) {
        logout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      refreshUserData();
    }
  }, [token]);

  const login = async (identifier: string, pass: string, captchaToken?: string) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('scalebulk_logged_out');
    }
    const isEmail = identifier.includes('@');
    const data = await ApiClient.auth.login({ 
      email: isEmail ? identifier : undefined,
      mobile: !isEmail ? identifier : undefined,
      identifier,
      password: pass,
      captchaToken
    });

    if (!data.token) {
      throw new Error(data.error || 'Authentication failed.');
    }

    ApiClient.setToken(data.token);
    setToken(data.token);
    setUser(data.user);
    const userWorkspaces = data.workspaces || [];
    setWorkspaces(userWorkspaces);
    const active = data.primaryWorkspace || data.workspaces?.[0] || null;
    setCurrentWorkspace(active);
    if (active) {
      ApiClient.setWorkspaceId(active.id);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('scalebulk_token', data.token);
      localStorage.setItem('scalebulk_user_data', JSON.stringify(data.user));
      localStorage.setItem('scalebulk_workspaces_list', JSON.stringify(userWorkspaces));
      if (active) {
        localStorage.setItem('scalebulk_workspace_data', JSON.stringify(active));
        localStorage.setItem('scalebulk_workspace_id', active.id);
      }
    }
  };

  const register = async (
    name: string,
    email: string,
    pass: string,
    company: string,
    mobile?: string,
    workspaceName?: string,
    captchaToken?: string,
    extraFields?: {
      country?: string;
      jobTitle?: string;
      website?: string;
      companySize?: string;
      confirmPassword?: string;
      termsAccepted?: boolean;
      plan_id?: string;
    }
  ) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('scalebulk_logged_out');
    }
    const data = await ApiClient.auth.register({
      full_name: name,
      email,
      password: pass,
      confirm_password: extraFields?.confirmPassword,
      company_name: company,
      mobile,
      workspace_name: workspaceName,
      country: extraFields?.country,
      job_title: extraFields?.jobTitle,
      website: extraFields?.website,
      company_size: extraFields?.companySize,
      terms_accepted: extraFields?.termsAccepted !== undefined ? extraFields.termsAccepted : true,
      plan_id: extraFields?.plan_id,
      captchaToken
    });

    // If client requires email/mobile verification or admin approval, do NOT auto-login
    if (data.requires_approval || data.status === 'PENDING_APPROVAL' || data.requires_email_verification) {
      return data;
    }

    // If superadmin auto-active account was created
    if (data.token) {
      ApiClient.setToken(data.token);
      setToken(data.token);
      setUser(data.user);
      const ws = data.workspace || null;
      if (ws) {
        setWorkspaces([ws]);
        setCurrentWorkspace(ws);
        ApiClient.setWorkspaceId(ws.id);
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('scalebulk_token', data.token);
        localStorage.setItem('scalebulk_user_data', JSON.stringify(data.user));
        if (ws) {
          localStorage.setItem('scalebulk_workspaces_list', JSON.stringify([ws]));
          localStorage.setItem('scalebulk_workspace_data', JSON.stringify(ws));
          localStorage.setItem('scalebulk_workspace_id', ws.id);
        }
      }
    }

    return data;
  };

  const forgotPassword = async (identifier: string, captchaToken?: string) => {
    return ApiClient.auth.forgotPassword({ identifier, captchaToken });
  };

  const resetPassword = async (payload: { token?: string; identifier?: string; otp_code?: string; new_password?: string; password?: string }) => {
    return ApiClient.auth.resetPassword(payload);
  };

  const verifyOtp = async (payload: { identifier: string; channel: 'EMAIL' | 'SMS'; purpose: string; code: string }) => {
    return ApiClient.auth.verifyOtp(payload);
  };

  const resendOtp = async (payload: { identifier: string; channel: 'EMAIL' | 'SMS'; purpose: string; captchaToken?: string }) => {
    return ApiClient.auth.resendOtp(payload);
  };

  const logout = () => {
    ApiClient.setToken(null);
    ApiClient.setWorkspaceId(null);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('scalebulk_token');
      localStorage.removeItem('scalebulk_user_data');
      localStorage.removeItem('scalebulk_workspaces_list');
      localStorage.removeItem('scalebulk_workspace_data');
      localStorage.removeItem('scalebulk_workspace_id');
      localStorage.removeItem('scalebulk_active_tab');
      localStorage.setItem('scalebulk_logged_out', 'true');
    }
    setToken(null);
    setUser(null);
    setWorkspaces([]);
    setCurrentWorkspace(null);
    try {
      ApiClient.auth.logout().catch(() => {});
    } catch {}
    if (typeof window !== 'undefined') {
      window.location.replace('/');
    }
  };

  const switchWorkspace = (workspaceId: string) => {
    const target = workspaces.find(w => w.id === workspaceId);
    if (target) {
      setCurrentWorkspace(target);
      ApiClient.setWorkspaceId(target.id);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('scalebulk_workspace_data', JSON.stringify(target));
        localStorage.setItem('scalebulk_workspace_id', target.id);
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      workspaces,
      currentWorkspace,
      token,
      isLoading,
      login,
      register,
      forgotPassword,
      resetPassword,
      verifyOtp,
      resendOtp,
      logout,
      switchWorkspace,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
